import { useEffect, useRef, useState } from 'react';
import { Search, Loader2, X, Sparkles, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AtlasSearchResults } from '@/api/knowledgeAtlas';

interface AtlasSearchProps {
  onSearch: (query: string) => void;
  searching: boolean;
  results: AtlasSearchResults | null;
  onClear: () => void;
  /** Fly to / select a specific document from the results affordance. */
  onSelectDocument: (documentId: number) => void;
  /** Fly to / spotlight a specific district from the results affordance. */
  onSelectDistrict: (districtId: number) => void;
}

/**
 * "Ask the atlas" — a guided query experience in the header. Submitting embeds
 * the query and asks the parent to highlight + frame the matches on the map.
 * This component owns the input and a results panel that leads with a synthesized
 * one-sentence ANSWER (when the backend supplies one), then each match with the
 * terms it matched on ("matched on: detention, easement"). Calm, light, crisp.
 */
export function AtlasSearch({
  onSearch,
  searching,
  results,
  onClear,
  onSelectDocument,
  onSelectDistrict,
}: AtlasSearchProps) {
  const [value, setValue] = useState('');
  const [showResults, setShowResults] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const submit = () => {
    const q = value.trim();
    if (!q) return;
    setShowResults(true);
    onSearch(q);
  };

  const clear = () => {
    setValue('');
    setShowResults(false);
    onClear();
  };

  // Reveal results when they arrive.
  useEffect(() => {
    if (results) setShowResults(true);
  }, [results]);

  // Dismiss the dropdown on outside click.
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const hasResults =
    results && (results.documents.length > 0 || results.districts.length > 0);
  const answer = results?.answer?.trim() || null;
  // The atlas is "in search" once a query has run, so the clear button can
  // restore the normal view even after the dropdown is dismissed.
  const inSearch = !!results || searching;

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border bg-white px-3 transition-colors',
          inSearch ? 'border-slate-300' : 'border-slate-200',
          'focus-within:border-slate-400'
        )}
      >
        {searching ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />
        ) : (
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
        )}
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') clear();
          }}
          onFocus={() => results && setShowResults(true)}
          placeholder="Ask the atlas…"
          className="h-9 w-full bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
        />
        {(value || inSearch) && (
          <button
            type="button"
            onClick={clear}
            className="rounded p-0.5 text-slate-400 transition-colors hover:text-slate-600"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {showResults && results && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+8px)] z-30 overflow-hidden rounded-lg border border-slate-200 bg-white"
          style={{ boxShadow: '0 16px 48px -20px rgba(15,23,42,0.25)' }}
        >
          {/* Synthesized answer headline ("the city's brain" speaking). */}
          {answer && (
            <div className="flex items-start gap-2 border-b border-slate-100 bg-amber-50/40 px-4 py-3">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
              <p className="text-[13px] leading-snug text-slate-700">{answer}</p>
            </div>
          )}

          {!hasResults ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">
              {answer ? 'No specific landmarks matched' : 'No matches found'}
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto py-2">
              {results.districts.length > 0 && (
                <div className="px-2 pb-1">
                  <div className="px-2 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
                    Districts
                  </div>
                  {results.districts.map((d) => (
                    <button
                      key={`district-${d.id}`}
                      type="button"
                      onClick={() => {
                        onSelectDistrict(d.id);
                        setShowResults(false);
                      }}
                      className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-slate-100"
                    >
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-sm text-slate-700">
                          <MapPin className="h-3 w-3 shrink-0 text-slate-400" />
                          <span className="truncate">{d.label}</span>
                        </span>
                        <MatchedTerms terms={d.matchedTerms} />
                      </span>
                      <Score value={d.score} />
                    </button>
                  ))}
                </div>
              )}
              {results.documents.length > 0 && (
                <div className="px-2 pt-1">
                  <div className="px-2 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">
                    Documents
                  </div>
                  {results.documents.map((doc) => (
                    <button
                      key={`doc-${doc.id}`}
                      type="button"
                      onClick={() => {
                        onSelectDocument(doc.id);
                        setShowResults(false);
                      }}
                      className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-slate-100"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm text-slate-700">
                          {doc.title}
                        </span>
                        <MatchedTerms terms={doc.matchedTerms} />
                      </span>
                      <Score value={doc.score} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MatchedTerms({ terms }: { terms?: string[] }) {
  if (!terms || terms.length === 0) return null;
  return (
    <span className="mt-0.5 block truncate text-[11px] text-slate-400">
      matched on: <span className="text-slate-500">{terms.join(', ')}</span>
    </span>
  );
}

function Score({ value }: { value: number }) {
  return (
    <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-slate-500">
      {Math.round(value * 100)}%
    </span>
  );
}
